<div class="container mx-auto px-4" [class.dark]="themeService.isDark()">
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

    <!-- Calculator Form -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
      <h2 class="text-lg font-bold text-gray-800 dark:text-white mb-6">Calculate Graham Price</h2>

      <!-- Form Fields -->
      <form [formGroup]="calculatorForm" (ngSubmit)="calculateGrahamPrice()">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

          <!-- Left Column -->
          <div class="space-y-4">

            <!-- Current EPS -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                EPS (TTM) <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <span class="absolute left-3 top-2.5 text-gray-500 dark:text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  formControlName="eps"
                  placeholder="Enter current EPS"
                  step="0.0001"
                  min="0"
                  class="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  [class.border-red-500]="calculatorForm.get('eps')?.invalid && calculatorForm.get('eps')?.touched"
                />
              </div>
              <div *ngIf="calculatorForm.get('eps')?.invalid && calculatorForm.get('eps')?.touched"
                   class="text-red-500 text-sm mt-1">
                EPS is required and must be positive
              </div>
            </div>

              <!-- Median PE -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Median PE Ratio <span class="text-red-500">*</span>
              </label>
              <input
                type="number"
                formControlName="medianPE"
                placeholder="Enter median PE ratio"
                step="0.01"
                min="0"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                [class.border-red-500]="calculatorForm.get('medianPE')?.invalid && calculatorForm.get('medianPE')?.touched"
              />
              <div *ngIf="calculatorForm.get('medianPE')?.invalid && calculatorForm.get('medianPE')?.touched"
                   class="text-red-500 text-sm mt-1">
                Median PE is required and must be positive
              </div>
              <!-- PE Warning for high values -->
              <div *ngIf="calculatorForm.get('medianPE')?.value && calculatorForm.get('medianPE')?.value > 25"
                   class="text-orange-600 text-sm mt-1 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded">
                <svg class="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                </svg>
                PE is higher than normal, ensure due diligence as this might not stay this high!
              </div>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Typically between 5-25 for most stocks
              </p>
            </div>
            

          </div>

          <!-- Right Column -->
          <div class="space-y-4">

          <!-- Book Value Per Share -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Book Value Per Share (BVPS) <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <span class="absolute left-3 top-2.5 text-gray-500 dark:text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  formControlName="bookValue"
                  placeholder="Enter book value per share"
                  step="0.0001"
                  min="0"
                  class="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  [class.border-red-500]="calculatorForm.get('bookValue')?.invalid && calculatorForm.get('bookValue')?.touched"
                />
              </div>
              <div *ngIf="calculatorForm.get('bookValue')?.invalid && calculatorForm.get('bookValue')?.touched"
                   class="text-red-500 text-sm mt-1">
                Book Value is required and must be positive
              </div>
            </div>

            <!-- P/B Ratio (Fixed at 1.5) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Price-to-Book (P/B) Ratio
              </label>
              <input
                type="number"
                value="1.5"
                disabled
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed"
              />
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Fixed multiplier of 1.5 as per Graham's formula
              </p>
            </div>

          </div>

        </div>

        <!-- Error Message -->
        <div *ngIf="errorMessage" class="mt-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div class="flex items-center">
            <svg class="w-5 h-5 text-red-500 dark:text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
            </svg>
            <span class="text-red-700 dark:text-red-400">{{ errorMessage }}</span>
          </div>
        </div>

        <!-- Calculate Button -->
        <div class="mt-6">
          <button
            type="submit"
            class="w-full text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-lg px-5 py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            [disabled]="calculatorForm.invalid || isCalculating">
           
            {{ isCalculating ? 'Calculating...' : 'Calculate Graham Price' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Results Panel -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
      <h2 class="text-lg font-bold text-gray-800 dark:text-white mb-6">Calculation Results</h2>

      <div *ngIf="!grahamPrice && !errorMessage" class="text-center py-12">
        <svg class="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6l-6 10V7z"></path>
        </svg>
        <p class="text-gray-500 dark:text-gray-400 text-lg">Enter values and click calculate to see results</p>
      </div>

      <div *ngIf="grahamPrice" class="space-y-6">
        <!-- Graham Price Result -->
        <div class="text-center p-6 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Graham Remix Sticker Price</h3>
          <div class="text-4xl font-bold text-green-600 dark:text-green-400">${{ grahamPrice | number:'1.2-2' }}</div>
          <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">Original Graham Price: {{ ogGrahamPrice | number:'1.2-2' }}</p>
        </div>

        <!-- Buy Price (50% MOS) -->
          <div class="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Buy Price (50% Discount)</h3>
            <div class="text-4xl font-bold text-orange-600 dark:text-orange-400 mb-2">
              ${{ grahamPrice*0.5 | number:'1.2-2' }}
            </div>
            <p class="text-gray-600 dark:text-gray-400">Discounted buy price to protect against bad estimates</p>
          </div>

        <!-- Current Price Comparison (if stock selected) -->
        <div *ngIf="selectedStock && currentPrice" class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <h4 class="font-semibold text-gray-700 dark:text-gray-300 mb-3">Price Comparison</h4>
          <div class="grid grid-cols-2 gap-4">
            <div class="text-center">
              <p class="text-sm text-gray-600 dark:text-gray-400">Current Price</p>
              <p class="text-xl font-semibold text-gray-900 dark:text-white">${{ currentPrice | number:'1.2-2' }}</p>
            </div>
            <div class="text-center">
              <p class="text-sm text-gray-600 dark:text-gray-400">Graham Price</p>
              <p class="text-xl font-semibold text-green-600 dark:text-green-400">${{ grahamPrice | number:'1.2-2' }}</p>
            </div>
          </div>

          <div class="mt-4 text-center">
            <div *ngIf="currentPrice < grahamPrice" class="text-green-600 dark:text-green-400">
              <svg class="w-5 h-5 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
              <span class="font-semibold">Potentially Undervalued</span>
              <p class="text-sm mt-1">
                Current price is {{ ((grahamPrice - currentPrice) / currentPrice * 100) | number:'1.1-1' }}% below Graham price
              </p>
            </div>
            <div *ngIf="currentPrice >= grahamPrice" class="text-red-600 dark:text-red-400">
              <svg class="w-5 h-5 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
              </svg>
              <span class="font-semibold">Potentially Overvalued</span>
              <p class="text-sm mt-1">
                Current price is {{ ((currentPrice - grahamPrice) / grahamPrice * 100) | number:'1.1-1' }}% above Graham price
              </p>
            </div>
          </div>
        </div>

        <!-- Calculation Breakdown -->
        <div class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <h4 class="font-semibold text-gray-700 dark:text-gray-300 mb-3">Calculation Breakdown</h4>
          <div class="text-sm space-y-2">
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Median PE:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ calculatorForm.get('medianPE')?.value | number:'1.1-2' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">P/B Ratio:</span>
              <span class="font-medium text-gray-900 dark:text-white">1.5</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">EPS:</span>
              <span class="font-medium text-gray-900 dark:text-white">${{ calculatorForm.get('eps')?.value | number:'1.2-4' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Book Value:</span>
              <span class="font-medium text-gray-900 dark:text-white">${{ calculatorForm.get('bookValue')?.value | number:'1.2-4' }}</span>
            </div>
            <hr class="my-2 border-gray-300 dark:border-gray-600">
            <div class="flex justify-between font-semibold">
              <span class="text-gray-700 dark:text-gray-300">Formula Result:</span>
              <span class="text-gray-900 dark:text-white">√({{ calculatorForm.get('medianPE')?.value }} × 1.5 × {{ calculatorForm.get('eps')?.value }} × {{ calculatorForm.get('bookValue')?.value }})</span>
            </div>
          </div>
        </div>

        <!-- Investment Analysis -->
          <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <div class="bg-white dark:bg-gray-800 rounded-md p-4 mt-4">
              <h4 class="font-semibold text-gray-800 dark:text-white mb-2">Ben Graham's Margin of Safety Discount</h4>
              <p class="text-gray-700 dark:text-gray-300 text-sm">
                The buy price represents a 50% discount to the calculated sticker price, providing a margin of safety
                to protect against estimation errors and market volatility. This conservative approach helps ensure
                you're buying at a truly attractive price.
              </p>
            </div>
          </div>
      </div>
    </div>
  </div>

  <!-- Information Section -->
  <div class="mt-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
    <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-6">About the Graham Remix Price Method</h2>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">What is Graham Price?</h3>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          The Graham Price formula was developed by Benjamin Graham, known as the "father of value investing."
          It provides a method to calculate the intrinsic value of a stock based on its earnings and book value.
        </p>
        <p class="text-gray-600 dark:text-gray-400">
          The original formula uses a conservative approach with fixed constant of 22.5 which is was Graham's "fair" market
          PE of 15 times P/B multiplier of 1.5 (15 * 1.5 = 22.5) making it conservative and suitable method for
          identifying potentially undervalued stocks. If price is below the Sticker Price it is undervalued.
        </p><br/>
        <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">How to Use</h3>
        <ul class="text-gray-600 dark:text-gray-400 space-y-2">
          <li class="flex items-start">
            <svg class="w-5 h-5 text-green-500 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
            <span>Search for a stock to auto-populate data or enter values manually</span>
          </li>
          <li class="flex items-start">
            <svg class="w-5 h-5 text-green-500 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
            <span>Ensure all values are positive (negative values will cause errors)</span>
          </li>
          <li class="flex items-start">
            <svg class="w-5 h-5 text-green-500 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
            <span>Compare the result with the current market price</span>
          </li>
          <li class="flex items-start">
            <svg class="w-5 h-5 text-green-500 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
            <span>Consider stocks trading below Graham price as potentially undervalued</span>
          </li>
        </ul>
      </div>
      <div>
        <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">Why the Remix?</h3>
        <p class="text-gray-600 dark:text-gray-400">In today's market the average PE varies widely based on the type of company.
          Think asset heavy (manufacturing) vs cash rich (software) so we adjust our valuation based on the specific
          Company's performace and not the overall market.
          Market PE averages are also up since Graham's time and the average now sits around 17.5.</p><br/>
          <p class="text-gray-600 dark:text-gray-400">WSY Graham Remix uses the Company's median PE over the last 10 years with the 1.5 multiple to get a more company specific price
          instead of one flat 15 PE for all companies.</p> <br/>

      </div>
    </div>
  </div>
</div>
